export const CUSTOMER_DOCUMENT_ALLOWED_MEDIA_TYPES = [
  'application/pdf',
  'image/tiff',
  'image/png',
  'image/jpeg',
] as const;

export const CUSTOMER_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

export type CustomerDocumentUploadStatus =
  | 'validating'
  | 'uploading'
  | 'pending_scan'
  | 'accepted'
  | 'quarantined'
  | 'failed'
  | 'cancelled';

export type CustomerDocumentUploadFailureCode =
  | 'device_file_not_allowed'
  | 'empty_file'
  | 'file_too_large'
  | 'unsupported_media_type'
  | 'fixture_mismatch'
  | 'synthetic_transfer_failed'
  | 'synthetic_scan_quarantined'
  | 'cancelled';

export type CustomerDocumentCandidate = {
  clientFileId: string;
  name: string;
  format: string;
  mediaType: string;
  sizeBytes: number;
  sizeLabel: string;
  source:
    | {kind: 'synthetic_fixture'; fixtureId: string}
    | {kind: 'browser_file'; file: File};
};

export type CustomerDocumentValidation =
  | {ok: true}
  | {ok: false; code: CustomerDocumentUploadFailureCode; message: string};

export type CustomerDocumentUploadSnapshot = {
  uploadId: string;
  clientFileId: string;
  status: CustomerDocumentUploadStatus;
  progress: number;
  message: string;
  retryable: boolean;
  canCancel: boolean;
  failureCode?: CustomerDocumentUploadFailureCode;
};

export type CustomerDocumentUploadOperation = {
  uploadId: string;
  cancel: () => void;
  completion: Promise<CustomerDocumentUploadSnapshot>;
};

export interface CustomerDocumentUploadAdapter {
  readonly kind: 'synthetic_local' | 'backend';
  validate(candidate: CustomerDocumentCandidate): CustomerDocumentValidation;
  start(
    candidate: CustomerDocumentCandidate,
    onUpdate: (snapshot: CustomerDocumentUploadSnapshot) => void,
  ): CustomerDocumentUploadOperation;
}

export const SYNTHETIC_CUSTOMER_DOCUMENT_FIXTURES = [
  {
    clientFileId: 'fixture-summons',
    name: 'synthetic-summons.pdf',
    format: 'PDF',
    mediaType: 'application/pdf',
    sizeBytes: 240 * 1024,
    sizeLabel: '240 KB',
    source: {kind: 'synthetic_fixture', fixtureId: 'summons'} as const,
  },
  {
    clientFileId: 'fixture-affidavit',
    name: 'synthetic-affidavit.tiff',
    format: 'TIFF',
    mediaType: 'image/tiff',
    sizeBytes: 110 * 1024,
    sizeLabel: '110 KB',
    source: {kind: 'synthetic_fixture', fixtureId: 'affidavit'} as const,
  },
  {
    clientFileId: 'fixture-service-address',
    name: 'synthetic-service-address.jpg',
    format: 'JPEG',
    mediaType: 'image/jpeg',
    sizeBytes: 320 * 1024,
    sizeLabel: '320 KB',
    source: {kind: 'synthetic_fixture', fixtureId: 'service-address'} as const,
  },
] satisfies readonly CustomerDocumentCandidate[];

const fixtureById = new Map<string, CustomerDocumentCandidate>(
  SYNTHETIC_CUSTOMER_DOCUMENT_FIXTURES.map((fixture) => [fixture.source.fixtureId, fixture]),
);

let nextSyntheticUpload = 1;

export class SyntheticLocalCustomerDocumentUploadAdapter implements CustomerDocumentUploadAdapter {
  readonly kind = 'synthetic_local' as const;
  private readonly attempts = new Map<string, number>();

  constructor(private readonly stepDelayMs = 500) {}

  validate(candidate: CustomerDocumentCandidate): CustomerDocumentValidation {
    if (candidate.source.kind !== 'synthetic_fixture') {
      return {
        ok: false,
        code: 'device_file_not_allowed',
        message: 'Device files are not available in this synthetic preview. Use only a supplied local fixture. No file bytes were read or sent.',
      };
    }
    if (!Number.isInteger(candidate.sizeBytes) || candidate.sizeBytes < 1) {
      return {ok: false, code: 'empty_file', message: `${candidate.name} was not added because it is empty. No file bytes were read or sent.`};
    }
    if (candidate.sizeBytes > CUSTOMER_DOCUMENT_MAX_BYTES) {
      return {ok: false, code: 'file_too_large', message: `${candidate.name} was not added. ${candidate.sizeLabel} exceeds the 25 MB per-file maximum. No file bytes were read or sent.`};
    }
    if (!CUSTOMER_DOCUMENT_ALLOWED_MEDIA_TYPES.includes(candidate.mediaType as typeof CUSTOMER_DOCUMENT_ALLOWED_MEDIA_TYPES[number])) {
      return {ok: false, code: 'unsupported_media_type', message: `${candidate.name} was not added. ${candidate.format} is not an accepted file type. Use only PDF, TIFF, PNG, or JPEG. No file bytes were read or sent.`};
    }
    const fixture = fixtureById.get(candidate.source.fixtureId);
    if (!fixture
      || fixture.clientFileId !== candidate.clientFileId
      || fixture.name !== candidate.name
      || fixture.mediaType !== candidate.mediaType
      || fixture.sizeBytes !== candidate.sizeBytes) {
      return {ok: false, code: 'fixture_mismatch', message: 'The supplied fixture metadata did not match the local synthetic catalog. Nothing was uploaded or stored.'};
    }
    return {ok: true};
  }

  start(candidate: CustomerDocumentCandidate, onUpdate: (snapshot: CustomerDocumentUploadSnapshot) => void): CustomerDocumentUploadOperation {
    const validation = this.validate(candidate);
    const uploadId = `SYN-UPLOAD-${String(nextSyntheticUpload++).padStart(4, '0')}`;
    if (!validation.ok) {
      const result: CustomerDocumentUploadSnapshot = {
        uploadId,
        clientFileId: candidate.clientFileId,
        status: 'failed',
        progress: 0,
        message: validation.message,
        retryable: false,
        canCancel: false,
        failureCode: validation.code,
      };
      onUpdate(result);
      return {uploadId, cancel: () => {}, completion: Promise.resolve(result)};
    }

    const controller = new AbortController();
    let terminal = false;
    const publish = (
      status: CustomerDocumentUploadStatus,
      progress: number,
      message: string,
      failureCode?: CustomerDocumentUploadFailureCode,
    ) => {
      const snapshot: CustomerDocumentUploadSnapshot = {
        uploadId,
        clientFileId: candidate.clientFileId,
        status,
        progress,
        message,
        retryable: ['failed', 'quarantined', 'cancelled'].includes(status),
        canCancel: ['validating', 'uploading', 'pending_scan'].includes(status),
        failureCode,
      };
      onUpdate(snapshot);
      return snapshot;
    };
    const pause = () => new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(resolve, this.stepDelayMs);
      controller.signal.addEventListener('abort', () => {
        window.clearTimeout(timer);
        reject(new DOMException('Synthetic upload cancelled.', 'AbortError'));
      }, {once: true});
    });

    const completion = (async () => {
      try {
        publish('validating', 0, 'Validating supplied synthetic metadata. No file bytes were read.');
        await pause();
        publish('uploading', 25, 'Synthetic transfer in progress. No file bytes were sent.');
        await pause();
        publish('uploading', 70, 'Synthetic transfer in progress. No file bytes were sent.');
        await pause();
        publish('pending_scan', 100, 'Synthetic transfer complete; pending synthetic scan. No file bytes were sent or stored.');
        await pause();

        const fixtureId = candidate.source.kind === 'synthetic_fixture' ? candidate.source.fixtureId : '';
        const attempt = (this.attempts.get(fixtureId) ?? 0) + 1;
        this.attempts.set(fixtureId, attempt);
        terminal = true;
        if (fixtureId === 'affidavit' && attempt === 1) {
          return publish('quarantined', 100, 'Held in synthetic quarantine and blocked from review. Retry the corrected fixture or remove it. No file bytes were sent or stored.', 'synthetic_scan_quarantined');
        }
        if (fixtureId === 'service-address' && attempt === 1) {
          return publish('failed', 0, 'The synthetic transfer failed safely. Retry or remove it. No file bytes were sent or stored.', 'synthetic_transfer_failed');
        }
        return publish('accepted', 100, 'Accepted after synthetic scan and ready for document review. No file bytes were sent or stored.');
      } catch (error) {
        terminal = true;
        if (error instanceof DOMException && error.name === 'AbortError') {
          return publish('cancelled', 0, 'Synthetic upload cancelled. Retry or remove it. No file bytes were sent or stored.', 'cancelled');
        }
        return publish('failed', 0, 'The synthetic upload failed safely. Retry or remove it. No file bytes were sent or stored.', 'synthetic_transfer_failed');
      }
    })();

    return {
      uploadId,
      cancel: () => {if (!terminal) controller.abort();},
      completion,
    };
  }
}
